import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import { listInventoryBalances } from "../api/inventory";

// Sayfa başına çekilen satır (backend üst sınırı 100).
const PAGE_LIMIT = 100;

const CATEGORIES = [
  { key: "raw", label: "Hammadde", desc: "Bobin, boya, lak, etiket ve koli stokları" },
];

const RAW_GROUPS = [
  { key: "bobin", label: "Bobin", types: ["paper"] },
  { key: "boya_lak", label: "Boya ve Lak", types: ["ink", "lak"] },
  { key: "etiket", label: "Etiket", types: ["label"] },
  { key: "koli", label: "Koli", types: ["box"] },
  { key: "tumu", label: "Tümü", types: ["paper", "ink", "lak", "label", "box", "shrink"] },
];

// Parti numarasındaki rezervasyon eki (ör. /R55) Excel'de gösterilmez.
function displayLotNo(lotNo) {
  const s = String(lotNo || "");
  return s.replace(/\/R\d+/i, "").trim();
}

const COLUMNS = [
  { header: "Malzeme Adı", width: 30, get: (r) => r.material_name || "" },
  { header: "Tedarikçi", width: 24, get: (r) => r.supplier_name || "" },
  { header: "Varyant", width: 34, get: (r) => r.material_variant_display_name || "" },
  { header: "Parti (Lot) No", width: 20, get: (r) => displayLotNo(r.lot_no) },
  { header: "Birim", width: 9, get: (r) => r.material_unit || "" },
  { header: "Sistem Miktarı", width: 15, get: (r) => Number(r.on_hand_qty || 0) },
  { header: "Sayılan Miktar", width: 15, get: () => "" },
  { header: "Fark", width: 12, get: () => "" },
  { header: "Açıklama", width: 26, get: () => "" },
];

function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function StockCountExcelDialog({ open, onClose }) {
  const [category, setCategory] = useState("");
  const [group, setGroup] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    if (!open) return;
    setCategory("");
    setGroup("");
    setBusy(false);
    setError("");
    setDone("");
  }, [open]);

  /** Seçilen gruptaki tüm bakiye satırlarını sayfalayarak toplar. */
  async function fetchAllRows(types) {
    const all = [];
    let page = 1;
    for (;;) {
      const data = await listInventoryBalances({
        raw_material_type: types.join(","),
        page,
        limit: PAGE_LIMIT,
      });
      const items = data?.items || [];
      all.push(...items);
      const total = Number(data?.total || 0);
      if (items.length < PAGE_LIMIT || all.length >= total) break;
      page += 1;
      if (page > 200) break; // güvenlik sınırı
    }
    return all;
  }

  async function handleCreate() {
    const selected = RAW_GROUPS.find((g) => g.key === group);
    if (!selected) return;

    setBusy(true);
    setError("");
    setDone("");
    try {
      const rows = await fetchAllRows(selected.types);
      if (!rows.length) {
        setError("Seçilen grupta stok kaydı bulunamadı.");
        return;
      }

      const aoa = [COLUMNS.map((c) => c.header), ...rows.map((r) => COLUMNS.map((c) => c.get(r)))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = COLUMNS.map((c) => ({ wch: c.width }));
      ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: COLUMNS.length - 1 } }) };
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stok Sayim");

      const fileName = `Stok_Sayim_${selected.label.replace(/\s+/g, "_")}_${todayStamp()}.xlsx`;

      // Chrome'da kayıt penceresi doğrudan Masaüstü'nde açılır; diğer tarayıcılarda
      // dosya tarayıcının indirme klasörüne iner.
      let savedToPicked = false;
      if (typeof window !== "undefined" && window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: fileName,
            startIn: "desktop",
            types: [{ description: "Excel", accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] } }],
          });
          const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          const writable = await handle.createWritable();
          await writable.write(new Blob([buf]));
          await writable.close();
          savedToPicked = true;
        } catch (e) {
          if (e?.name === "AbortError") { setBusy(false); return; } // kullanıcı vazgeçti
          savedToPicked = false;
        }
      }
      if (!savedToPicked) XLSX.writeFile(wb, fileName);

      setDone(`${fileName} oluşturuldu · ${rows.length} satır`);
    } catch (e) {
      setError(e?.response?.data?.error?.message || "Excel oluşturulamadı.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #065f46 0%, #059669 56%, #10b981 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 20 }}>▤</Box>
            <Box>
              <Typography variant="h6" fontWeight={800}>Stok Sayım Excel'i</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Sayım için stok listesini Excel olarak oluşturun.</Typography>
            </Box>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        <Stack spacing={2}>
          {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert> : null}
          {done ? <Alert severity="success" sx={{ borderRadius: 2 }}>{done}</Alert> : null}

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Typography fontWeight={800} sx={{ mb: 0.5 }}>1. Stok türü</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Sayımı yapılacak stok türünü seçin.</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {CATEGORIES.map((c) => (
                <Button
                  key={c.key}
                  variant={category === c.key ? "contained" : "outlined"}
                  onClick={() => { setCategory(c.key); setGroup(""); setDone(""); setError(""); }}
                  disabled={busy}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                >
                  {c.label}
                </Button>
              ))}
            </Stack>
          </Paper>

          {category === "raw" ? (
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>2. Hammadde grubu</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Excel'e alınacak hammadde grubunu seçin.</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {RAW_GROUPS.map((g) => (
                  <Button
                    key={g.key}
                    variant={group === g.key ? "contained" : "outlined"}
                    onClick={() => { setGroup(g.key); setDone(""); setError(""); }}
                    disabled={busy}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    {g.label}
                  </Button>
                ))}
              </Stack>
            </Paper>
          ) : null}

          {busy ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* Sayılan Miktar ve Fark sütunları elle doldurulmak üzere boş bırakılır.</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy} color="inherit">Kapat</Button>
          <Button
            onClick={handleCreate}
            disabled={busy || !group}
            variant="contained"
            sx={{ px: 2.5, borderRadius: 2, bgcolor: "#059669", boxShadow: "0 8px 18px rgba(5, 150, 105, 0.24)", "&:hover": { bgcolor: "#047857" } }}
          >
            {busy ? "Oluşturuluyor..." : "Excel Oluştur"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
